
import { namespace, Getter } from "vuex-class";
import { Component, Mixins, Watch } from "vue-property-decorator";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { eventqueue } from "@/store/eventqueue";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin";
import { FilteredEvent, EventType, CallWaitingPref } from "@/store/eventqueue/types";
import EventQueueAreaNotesIcon from "@/components/event-queue/EventQueueAreaNotesIcon.vue";
import { FeaturesList, UserPermissions } from "@/store/types";
import { get } from "lodash";
import LinkedTaskIcon from "@/components/alarm-queue/LinkedTaskIcon.vue";
import RestoreIcon from "@/components/event-queue/RestoreIcon.vue";
import api from "@/services/api.service";
import { CallQueue, PickupCall, CallClient } from "@/store/eventqueue/types";
import IntercomModal from "@/components/event-queue/IntercomModal.vue";
import moment from "moment";
import EventTypes from "../types/EventTypes";
import router from "@/router";

const Areas = namespace("areas");
const Eventqueue = namespace("eventqueue");
const License = namespace("license");

@Component({
	components: {
		VuePerfectScrollbar,
		"event-queue-area-notes-icon": EventQueueAreaNotesIcon,
		"linked-task-icon": LinkedTaskIcon,
		"restore-icon": RestoreIcon,
		"intercom-modal": IntercomModal,
	}
})
export default class AlarmQueue extends Mixins(RegionalHandlerMixin) {
	@Eventqueue.Getter getActiveEvents!: FilteredEvent[];
	@Eventqueue.Getter getCurrentSort: any;
	@Eventqueue.Action setDisplayedEvent: any;

	@Eventqueue.Mutation setHoverEvent: any;
	@Eventqueue.Mutation setCurrentSort: any;
	@Eventqueue.Mutation setCurrentSortChanged: any;
	@Eventqueue.Mutation setAllAlarmsSelected: (allSelected: boolean) => void;
	@Eventqueue.Mutation setAllAlarmsClicked: (clicked: boolean) => void;
	@Eventqueue.Mutation setSelectedAlarms: (data: number[]) => void;
	@Eventqueue.Mutation addSelectedAlarm: (data: number) => void;
	@Eventqueue.Mutation removeSelectedAlarm: (data: number) => void;

	@License.State licenseDetails: any;
	@License.State("isLoaded") isLicenseLoaded: Boolean;

	// Getter and action for toggling the map' display.
	@Eventqueue.Action setHideMapFlag: any;
	@Eventqueue.Getter("getHideMapFlag") hideMapFlag: boolean;

	// Getters for Bulk Alarm Handling data
	@Eventqueue.Getter("getBulkHandleShown") bulkHandleShown!: boolean;
	@Eventqueue.Getter("getAllAlarmsSelected") allAlarmsSelected!: boolean;
	@Eventqueue.Getter("getAllAlarmsClicked") allAlarmsClicked!: boolean;
	@Eventqueue.Getter("getSelectedAlarms") selectedAlarms: number[];
	@Eventqueue.Getter("getRefreshQueue") refreshQueue: boolean;
	@Eventqueue.Action setRefreshQueue: any;

	@Getter("getDbFederationEnabled") dbFederationEnabled: any;
	@Getter("getFeaturesList") featuresList: FeaturesList;
	@Getter("getAlarmAgeWarningSeconds") alarmAgeWarningSeconds: any;
	@Getter("getUserId") currentUserId: number;
	@Getter("getPermissions") private permissions: UserPermissions;

	@Getter("getUserName") public currentUserName!: string;

	@Eventqueue.Action private getCallWaitingPrefs: () => Promise<void>;
	@Eventqueue.State private callWaitingPrefs: CallWaitingPref[];
	@Eventqueue.State private callQueue: CallQueue[];
	@Eventqueue.State private callClients: CallClient[];
	@Eventqueue.State private showIntercomModal: boolean;
	@Eventqueue.Mutation private setShowIntercomModal: (visible: boolean) => void;
	@Getter private getPermissions: UserPermissions;

	private selectedCallTemplate : PickupCall = {
		userId: 0,
		callId: 0,
		eventId: 0,
		areaName: null,
		deviceId: 0,
		deviceEndPoint: null,
		title: null
	};

	private selectedCall: PickupCall = { ...this.selectedCallTemplate };

	// used to show loading spinner when claiming or releaseing an event
	private claimingEventId: number = null;

	// empty array with a blank entry, forcing the index to start at 1 for users
	private eventQueueUserList: string[] = [""];

	// show or hide Event Claimed Warning
	private showEventClaimedWarningModal: boolean = false;

	// used to store the event data so it can be triggered from the Claim Warning Modal
	private tempEventData: FilteredEvent = null;

	public columns: any[] = [
		{
			title: "Started",
			sort: 1,
			isDefault: true,
			currentSort: 0,
			description: "How long ago the event began",
			reversed: true
		},
		{
			title: "Location",
			sort: 2,
			isDefault: false,
			currentSort: 0,
			description: "Area affected by alarm",
			reversed: false
		},
		{
			title: "Event",
			sort: 3,
			isDefault: false,
			currentSort: 0,
			description: "Alarm title",
			reversed: false
		},
		{
			title: "Priority",
			sort: 4,
			isDefault: true,
			currentSort: 0,
			description: "Event priority",
			reversed: false
		},
		{
			title: "Count",
			sort: 5,
			isDefault: false,
			currentSort: 0,
			description: "Number of alarms in event",
			reversed: false
		}
	];

	private get isPickUpOnNameEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "PickUpOnName"]);
	}

	private get isMissedTargetResponseTimeIcon(): boolean {
		return get(this.featuresList, ["Alarms", "MissedTargetResponseTimeIcon"]);
	}

	private get isTypeColumnEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "ServerEventTitleInQueue"]);
	}

	private get isDisplayIntercomInViewsEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Intercom", "DisplayInViews"])
			&& get(this.featuresList, ["Alarms", "EventQueue", "Calls"]);
	}

	// Feature flag toggle for when placing tours into event queue is enabled.
	private get isDisplayScheduleToursInAlarmsEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "ShowToursInEventQueue"]);			
	}



	private get isUserInViews(): boolean {
		return router.currentRoute && router.currentRoute.name == "views";
	}

	//Required as using isTypeColumnEnabled causes the cells to missalign if toggling the feature whilst someone is looking at the queue
	//This is because the column header is only added on load
	private get isTypeColumnVisible(): boolean {
		const result = this.columns.filter(c => c.title == "Type");
		return result.length != 0;
	}

	public async mounted() {
		await this.getCallWaitingPrefs();
	}

	public get events() {
		if (this.getActiveEvents == null) {
			return null;
		}
		

		let filteredEvents = this.getActiveEvents.filter(
			q =>
				((q.eventTypeID == EventTypes.Alarm || 
					(
						q.eventTypeID == EventTypes.Call &&
						((!this.isUserInViews && this.showIntercomAlarmQueue) || (this.isUserInViews && this.isDisplayIntercomInViewsEnabled))
					) 				
					||
					( 						
						q.eventTypeID == EventTypes.Patrol && 
						(this.isDisplayScheduleToursInAlarmsEnabled && !this.isUserInViews)
					)
				) 
					&& 
				q.inProcessing == false &&
				q.unParkAt == null &&
				q.isPermanentParked == false &&
				q.readyForPostProcessing == null &&
				q.groupID)
		);

		return filteredEvents;
	}

	private async pickupCall(event: FilteredEvent) {		
		if (this.showIntercomAlarmQueue) 
		{
			//get the right call for this event
			let call = this.callQueue.find(call => call.eventId === event.eventID);
			if (call) {
				const currentCaller: PickupCall = {
					userId: this.currentUserId,
					callId: call.callId,
					eventId: call.eventId,
					areaName: null,
					deviceId: call.deviceId,
					deviceEndPoint: call.deviceEndPoint,
					title: call.title
				}
				
				const res = await api.setCaller(currentCaller);
				if (res?.status == 200) {			
					this.selectedCall = currentCaller;
			
					this.setShowIntercomModal(true);
				}
				else {
					console.error(`Couldn't set caller for CallId: ${ call.callId }`);
				}		
			}
			else {
				console.warn(`Could not find call for EventID: ${ event.eventID }`);
			}
		}			
	}

	public get showIntercomAlarmQueue(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Intercom", "DisplayInQueue"]) 
			&& get(this.featuresList, ["Alarms", "EventQueue", "Calls"]);
	}

	private getCallWaitingValue(prefId: number, callWaitTime: string): boolean {		
		let value = this.callWaitingPrefs?.find(pref => pref.prefId == prefId)?.prefValue;

		return value !== null && value < this.escapedTime(callWaitTime);
	}

	/**
     * @summary computed property which will return the class of the pickup call button
     * depending on the wait time to set the colour.
     */	
    private callWaiting(call: FilteredEvent): string {
				
		//(299) Intercom - Red call waiting
		if (this.getCallWaitingValue(299, call.created))
			return "incoming-call-red";
		//(298) Intercom - Amber call waiting)
		else if (this.getCallWaitingValue(298, call.created))
			return "incoming-call-amber";
		//(297) Intercom - Green call waiting
		else if (this.getCallWaitingValue(297, call.created))
			return "incoming-call-green";
		else
			return "";

    } 
	
	private escapedTime(end: string) : number {
		let now = moment(new Date()); //todays date
		let duration = moment.duration(now.diff(end));
		let seconds = duration.asSeconds();

		let waitTime = seconds;		

		//wait time in seconds
		return waitTime;
	}

	private get availableClients(): number {
		return this.callClients.filter(x => x.userId == null).length ?? 0;
	}

	private get activeCalls(): number {
		return this.callQueue.filter(x => x.pickedUp).length ?? 0;
	}

	/**
	 * Permission and feature check
	 */
	private get canViewSystemEvents(): boolean {
		return (
			get(this.featuresList, ["Alarms", "EventQueue", "SystemEvents"]) &&
			(this.getPermissions.isAdmin || this.getPermissions.canViewSystemEvents)
		);
	}

	public get showDispatchedGuards(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Alarms", "DispatchedGuards"]);
	}

	// Feature flag for map display toggling functionality.
	private get isToggleMapEnabled(): boolean {
		return get(this.featuresList, ["Alarms", "Maps", "ToggleEventQueue"]);
	}

	public get isRestoredIconEnabled() {
		return get(this.featuresList, ["Alarms", "EventQueue", "RestoreIcon"]);
	}

	public created() {
		if (this.isTypeColumnEnabled) {
			this.columns.splice(2, 0, {
				title: "Type",
				sort: 7,
				isDefault: false,
				currentSort: 0,
				description: "The type of event that triggered the alarm",
				reversed: false
			});
		}
		if (this.dbFederationEnabled) {
			this.columns.push({
				title: "Region",
				sort: 6,
				isDefault: false,
				currentSort: 0,
				description: "Region of origin",
				reversed: false
			});
		}

		if (this.claimEventsFeatureAvailable) {
			this.columns.push({
				title: "Claimed By",
				sort: 9,
				isDefault: false,
				currentSort: 0,
				description: "Users who had claimed an event",
				reversed: false
			});
		}
	}

	public hoverEvent(event: FilteredEvent) {
		this.setHoverEvent(event);
	}

	public unhoverEvent(event: FilteredEvent) {
		this.setHoverEvent({});
	}

	public columnClass(column: any) {
		let currentSort = this.getCurrentSort;

		if (currentSort.sort == 0 && column.isDefault) {
			return "";
		} else if (currentSort.sort == column.sort) {
			return currentSort.desc ? "sortDesc" : "sortAsc";
		}
	}

	public sortColumnClick(clickColumn: any) {
		let { sort, reversed: defaultDesc, desc: existingDesc } = this.getCurrentSort;

		if (clickColumn.sort != sort) {
			this.setCurrentSort({
				sort: clickColumn.sort,
				desc: clickColumn.reversed,
				reversed: clickColumn.reversed
			});
		} else {
			if (existingDesc == defaultDesc) {
				this.setCurrentSort({
					sort: clickColumn.sort,
					desc: !existingDesc,
					reversed: clickColumn.reversed
				});
			} else {
				this.setCurrentSort({
					sort: 0,
					desc: false,
					reversed: false
				});
			}
		}

		this.setCurrentSortChanged(true);
	}

	public alarmIconClick(event) {
		// Show map if hidden and toggle map feature is enabled.
		if (this.isToggleMapEnabled) {
			this.setHideMapFlag(false);
		}
		this.setDisplayedEvent(event);
	}

	public eventType(event: FilteredEvent): string {
		const title = event.serverEventTitle;
		return title == null ? "Unknown" : title.substring(0, Math.min(title.length, 50));
	}

	// Handles the toggling of a row select checkbox and set the AllAlarmsSelected in the state
	public alarmSelectedToggled(event) {
		// Logic to set to maintain (local) state of each row and update the state of AllSelected in the store
		if (event.target.checked) {
			// Row Selected
			// Add the array of checked rows
			this.addSelectedAlarm(parseInt(event.target.value));

			// Need to check if checking this row now means allAlarmsSelected should now be true
			if (this.selectedAlarms.length == this.events.length) {
				this.setAllAlarmsSelected(true);
			}
		} else {
			// Row Deselected
			// remove from the array of checked rows
			this.removeSelectedAlarm(parseInt(event.target.value));

			// As this is row deselected all allAlarmsSelected should must be false
			this.setAllAlarmsSelected(false);
		}
	}

	public isAlarmSelected(eventID: number): boolean {
		return this.selectedAlarms.includes(eventID);
	}

	// Checks if all the rows in the table are checked
	// Used when items are added / removed from the queue
	// Slightly different to onAllAlarmsSelected which is the event handler for the 'Select All' check box
	public allRowsChecked(): boolean {
		this.events.forEach(event => {
			if (!this.isAlarmSelected(event.eventID)) {
				return false;
			}
		});
		return true;
	}

	@Watch("allAlarmsSelected")
	public onAllAlarmsSelected(selectAll: boolean) {
		// Add all or remove from the arrray if the AllAlarms selected is changed in the store
		if (selectAll) {
			this.events.forEach(event => {
				if (this.selectedAlarms.indexOf(event.eventID) === -1) {
					this.addSelectedAlarm(event.eventID);
				}
			});
		} else {
			// Only reset all check boxes if the change to the state was due to the 'Select All' check box being clicked
			if (this.allAlarmsClicked) {
				this.setSelectedAlarms([]);
			}
		}

		// Finished handling the change so reset the setAllAlarmsClicked to false
		this.setAllAlarmsClicked(false);
	}

	@Watch("events")
	public eventsChanged(newVal: FilteredEvent[], oldVal: FilteredEvent[]) {
		// if there are more alarms than selected alarms
		// or the queue has been emptied  then 'select all' can't / shouldn't be checked
		if (this.events && (this.events.length > (this.selectedAlarms && this.selectedAlarms.length) || this.events.length == 0)) {
			this.setAllAlarmsSelected(false);
		} else {
			// set the 'select all' depending on whether all the correct rows are checked
			let allChecked: boolean = this.allRowsChecked();
			this.setAllAlarmsSelected(allChecked);
		}

		if (this.claimEventsFeatureAvailable) {
			this.updateEventQueueUsers(this.events);

			// if an event had been set to be claimed or released
			if (this.claimingEventId) {
				// find the event for the claim
				let newEvent = newVal.find(f => f.eventID == this.claimingEventId);
				let oldEvent = oldVal.find(f => f.eventID == this.claimingEventId);

				// if its state has changed from the previous state, clear claimingEventId so stop showing the loading spinner
				if (newEvent.claimedUserId != oldEvent.claimedUserId) {
					this.claimingEventId = null;
				}
			}
		}
	}

	@Watch("refreshQueue")
	public reloadQueue() {
		if (this.refreshQueue) {
			// clear the array of selected alarms
			this.setSelectedAlarms([]);

			// force the queue to update
			this.$forceUpdate();
			// stop the refresh cycle
			this.setRefreshQueue(false);
		}
	}

	private get claimEventsFeatureAvailable(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Claiming"]);
	}

	private get showEventClaimedWarning(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Claiming", "ShowClaimedWarning"]);
	}

	private get canProcessClaimedEvent(): boolean {
		return get(this.featuresList, ["Alarms", "EventQueue", "Claiming", "ProcessClaimedEvents"]);
	}

	private async claimUnclaimEvent(event: FilteredEvent) {
		if (this.claimEventsFeatureAvailable && this.permissions.canClaimEvents) {
			this.claimingEventId = event.eventID;
			if (event.claimedUserId && event.claimedUserId > 0) {
				if (event.claimedUserId == this.currentUserId) {
					await api.ReleaseEvent(event.eventID);
				}
			} else {
				await api.ClaimEvent(event.eventID);
			}
		}
	}

	private userCanProcessEvent(event: FilteredEvent) {

		// if event claiming feature is off, proceed with normal event handling logic
		if (!this.claimEventsFeatureAvailable) {
			this.handleEventQueueItem(event);
		}		

		// if feature is enabled, show modal warning user that the event has been claimed
		if (this.showEventClaimedWarning && event.claimedUserId && event.claimedUserId != this.currentUserId) {
			this.tempEventData = event;
			this.showEventClaimedWarningModal = true;
		}
		// dont show a message, check if the user can still process the event or not
		else {
			if (
				!event.claimedUserId ||
				event.claimedUserId == this.currentUserId ||
				(this.canProcessClaimedEvent && event.claimedUserId != this.currentUserId)
			) {
				this.handleEventQueueItem(event);
				return;
			}
		}

		//if this event is a call then pickup the call
		if (event.eventTypeID === EventTypes.Call)
		{
			this.pickupCall(event);
		}
	}

	private closeEventClaimedModal() {
		this.showEventClaimedWarningModal = false;
		this.tempEventData = null;
	}

	/**
	 * Compiles a list of all the users who have claimed an event
	 */
	private updateEventQueueUsers(events: FilteredEvent[]) {
		const users = events.map(m => m.claimedUserFullName);

		users.forEach(user => {
			if (!this.eventQueueUserList.includes(user)) {
				this.eventQueueUserList.push(user);
			}
		});
	}

	/**
	 * Gets an index for the users who have claimed an event to use for the CSS class
	 */
	private getEventQueueUserIndex(user) {
		return this.eventQueueUserList.findIndex(u => u == user);
	}

	/**
	 * When the component is being torn down, clears current area cameras.
	 */
	private beforeDestroy() {
		// reset
		this.eventQueueUserList = [""];
		this.claimingEventId = null;
	}
}
